import { useMemo, useCallback } from 'react';
import moment from 'moment';
import { TRADING_HOURS_CAPTIONS } from '../constants';

const possible24Hours = ['Open 24hrs', '24hrs', '24x7'];
const shortNames = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

const useTradingHours = data => {
  const getValidCaption = caption => {
    const match = TRADING_HOURS_CAPTIONS.find(({ label, value }) =>
      [label.toLowerCase(), value.toLowerCase()].includes(caption.toLowerCase())
    );
    return (match && match.value) || 'Open';
  };

  const sortHours = hours =>
    hours.sort((a, b) => {
      const a24 = moment(a.open, ['h:mm A']).format('HH:mm');
      const b24 = moment(b.open, ['h:mm A']).format('HH:mm');
      if (a24 < b24) {
        return -1;
      }
      if (a24 > b24) {
        return 1;
      }
      return 0;
    });

  const formatOpenTime = open => {
    let openTime = open.replace(/\s+/g, '');
    if (open.includes('00')) {
      const regAmPm = /(am|pm)/i;
      const regHour = /([^:\s]+):([^:\s]+)/g;

      const getAmPm = regAmPm.exec(open);
      const getHour = regHour.exec(open);

      openTime = `${getHour ? getHour[1] : ''}${getAmPm ? getAmPm[0] : ''}`;
    }

    return openTime.trim().toLowerCase();
  };

  const getNextOpeningHours = (day, hours) => {
    let pastToday;
    let tradingHours = [...hours];
    const closedIndex = tradingHours.reduce((agg, { day: dayName }, index) => {
      if (day === dayName) {
        return index;
      }
      return agg;
    }, 0);
    if (closedIndex) {
      const moveToEnd = tradingHours.splice(0, closedIndex);
      tradingHours = [...tradingHours, ...moveToEnd];
    }
    const getNextOpenDay = tradingHours.reduce((agg, trading) => {
      const { isToday, hasOpeningTimes, day: nextOpenDay } = trading || {};
      if (pastToday && hasOpeningTimes && nextOpenDay !== '24x7') {
        pastToday = false;
        return trading;
      }
      if (isToday) {
        pastToday = true;
        return agg;
      }
      return agg;
    }, null);

    if (getNextOpenDay) {
      const { hours: nextOpenHours, day: nextOpenDay } = getNextOpenDay || {};
      return nextOpenHours.reduce((agg, hour) => {
        const { caption, tag, open } = hour || {};
        if (caption !== 'Closed' && tag !== 'Closed' && !agg && open) {
          return `${open ? `${formatOpenTime(open)} ` : ''}${
            shortNames[nextOpenDay]
          }`;
        }
        if (nextOpenDay) {
          return nextOpenDay;
        }
        return agg;
      }, null);
    }
    return null;
  };

  const getDisplayTime = useCallback(test => {
    const { hours, day, isPublicHoliday } = test;
    const currentHours = ['Open', 'Closing Soon', 'Open Today'].reduce(
      (acc, tag) => {
        const current = hours.find(item => item.tag === tag);
        if (current && current.isOpen) {
          return current;
        }
        if (!acc && current) {
          return current;
        }
        return acc;
      },
      null
    );
    const openTagsWithOpenFalse = hours
      .filter(item => !item.isOpen && ['Open', 'Open Today'].includes(item.tag))
      .reduce((agg, item) => {
        return item;
      }, null);

    const { is_open: isOpen, caption, tag, open, close } =
      openTagsWithOpenFalse || currentHours || hours[0] || {};

    if (day === '24x7') return {};

    const is24hrsTag =
      (possible24Hours.includes(tag) || possible24Hours.includes(caption)) &&
      'Open 24hrs';

    const isClosedTag = [caption, tag].includes('Closed') && 'Closed';
    const isPubHolsDay = [day].includes('Pub Hols');

    const isClosingSoonTag = ['Closing Soon'].find(item => item === tag);

    let isOpenTodayTag = ['Open Today'].find(item => item === tag);

    let displayText = '';

    if (!isClosedTag) {
      if (open) {
        displayText += open;
      }
      if (close) {
        displayText += ` - ${close}`;
      }
    }

    const displayTimeText =
      tag && tag.trim() !== displayText.trim() ? displayText.trim() : '';

    if (isOpenTodayTag && open) {
      isOpenTodayTag = `Opens ${formatOpenTime(open)}`;
    }

    return {
      isOpen,
      showPublicHolidayText: isPublicHoliday && !isPubHolsDay,
      day: day.trim(),
      tag:
        isClosedTag ||
        isClosingSoonTag ||
        isOpenTodayTag ||
        is24hrsTag ||
        getValidCaption(caption || ''),
      text: displayTimeText,
    };
  }, []);

  const formattedHours = useMemo(() => {
    if (data) {
      const openingHours = data.filter(hours => hours.day);

      const tradingHoursObj = openingHours.reduce((aggregate, trading) => {
        const {
          day,
          is_today: isToday,
          is_public_holiday: isPublicHoliday,
          hours = [],
        } = trading;
        let validOpenTime = false;
        let hasOpeningTimes = false;
        const formatHours = hours.map(
          ({
            caption,
            open,
            close,
            tag,
            is_open: isOpen,
            original_text: text,
          }) => {
            validOpenTime =
              isOpen && (caption !== 'Closed' && tag !== 'Closed');
            hasOpeningTimes = caption !== 'Closed' && tag !== 'Closed';

            return {
              caption,
              open,
              close,
              tag,
              isOpen: isOpen && validOpenTime,
              text,
            };
          }
        );

        if (aggregate[day]) {
          return {
            ...aggregate,
            [day]: {
              ...aggregate[day],
              hours: sortHours([...aggregate[day].hours, ...formatHours]),
            },
          };
        }
        return {
          ...aggregate,
          [day]: {
            day,
            isToday,
            hasOpenTime: validOpenTime,
            hasOpeningTimes,
            isPublicHoliday,
            hours: [...formatHours],
          },
        };
      }, {});

      const tradingHoursArray = Object.keys(tradingHoursObj).map(
        key => tradingHoursObj[key]
      );

      const today = tradingHoursArray.find(
        ({ isToday, day }) => isToday && day !== '24x7'
      );

      const displayTime = today ? getDisplayTime(today) : {};

      const { tag, day } = displayTime || {};
      if (tag === 'Closed') {
        const nextOpenTime = getNextOpeningHours(day, tradingHoursArray);
        if (nextOpenTime) {
          displayTime.nextOpenDay = ` - Opens ${nextOpenTime}`;
        }
      }

      return {
        tradingHours: tradingHoursArray,
        displayTime,
      };
    }

    return {
      tradingHours: [],
      displayTime: {},
    };
  }, [data, getDisplayTime]);

  return formattedHours;
};

export default useTradingHours;
